import { useContext, useEffect } from 'react';
import { CONVERSATION_CONSTANTS } from './src-config-miaw-constants.js';
import { CAContext } from './src-contexts-CAContext.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { setChatSessionType } from './src-utils-session-storage-utils.js';
import { useMIAWChatEnd } from './src-hooks-miaw-useMIAWChatEnd.js';
import { useMIAWChatMessage } from './src-hooks-miaw-useMIAWChatMessage.js';
import { useMIAWChatRetrieve } from './src-hooks-miaw-useMIAWChatRetrieve.js';
import { useMIAWStartChat } from './src-hooks-miaw-useMIAWChatStart.js';
import { useCustomChatEvents } from './src-hooks-custom-hooks-useCustomChatEvents.js';

const useMIAWHooks = () => {
  const {
    setChatState,
    chatState: { chatWindow, chatSession }
  } = useContext(CAContext);
  const { setState: setMIAWContext } = useContext(MiawChatContext);
  const { sendChatEvents } = useCustomChatEvents();
  const { loading: miawChatStartLoading, trigger: triggerMiawStartChat, data: miawChatStartData, error: miawChatStartError } = useMIAWStartChat();
  const {
    data: miawChatRetrieveData,
    trigger: miawChatRetrieveTrigger,
    loading: miawChatRetrieveLoading,
    error: miawChatRetrieveError
  } = useMIAWChatRetrieve();
  const { data: miawChatEndData, trigger: triggerMIAWChatEnd, loading: miawChatEndLoading, error: miawChatEndError } = useMIAWChatEnd();
  const { data: miawChatMessageData, trigger: triggerMIAWChatMessage, loading: miawChatMessageLoading, error: miawChatMessageError } = useMIAWChatMessage();
  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      startMiawChatSession: triggerMiawStartChat
    }));
  }, [triggerMiawStartChat, setMIAWContext]);
  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      sendMiawChatMessage: triggerMIAWChatMessage
    }));
  }, [triggerMIAWChatMessage, setMIAWContext]);
  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      endMiawChatSession: triggerMIAWChatEnd
    }));
  }, [triggerMIAWChatEnd, setMIAWContext]);
  useEffect(() => {
    if (miawChatStartData) {
      setChatSessionType("miawChatSession");
      setChatState((prevState) => ({ ...prevState, chatSession: "miawChatSession" }));
      sendChatEvents("onChatStart", {});
    }
  }, [miawChatStartData, setChatState]);
  useEffect(() => {
    if (miawChatRetrieveData) {
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: miawChatRetrieveData.messages,
        conversationId: miawChatRetrieveData.conversationId,
        conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION,
        agentName: miawChatRetrieveData.agentName
      }));
      sendChatEvents("onSessionContinuation", {});
    }
  }, [miawChatRetrieveData, setMIAWContext]);
  useEffect(() => {
    if (miawChatEndData) {
      setChatState((prevState) => ({
        ...prevState,
        chatSession: "endChatSession",
        isPrivacyPolicyAccepted: false,
        unreadMessages: 0
      }));
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: [],
        conversationId: "",
        agentName: "",
        conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION,
        endChatData: miawChatEndData
        // Set end chat data in MIAW context
      }));
      sendChatEvents("onChatEnd", {});
    }
  }, [miawChatEndData]);
  useEffect(() => {
    const hasError = miawChatMessageError || miawChatRetrieveError;
    if (hasError) {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWApiError: true
      }));
    } else {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWApiError: false
      }));
    }
  }, [miawChatMessageError, miawChatRetrieveError]);
  useEffect(() => {
    if (chatSession === "initial") {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWApiError: false
      }));
    }
  }, [chatSession]);
  useEffect(() => {
    if (chatSession === "initial") {
      setMIAWContext((prevState) => ({
        ...prevState,
        endChatData: void 0,
        endChatLoading: false
      }));
    }
  }, [chatSession]);
  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      endChatLoading: miawChatEndLoading
    }));
  }, [miawChatEndLoading]);
  useEffect(() => {
    if (chatWindow === "minimized") {
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true }))
      }));
    }
  }, [chatWindow]);
  useEffect(() => {
    setMIAWContext((prevState) => ({
      ...prevState,
      isMIAWApiLoading: miawChatStartLoading
    }));
  }, [miawChatStartLoading]);
  useEffect(() => {
    if (miawChatEndError) {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWEndChatError: true
      }));
    }
  }, [miawChatEndError]);
  useEffect(() => {
    if (chatSession === "initial") {
      setMIAWContext((prevState) => ({
        ...prevState,
        isMIAWEndChatError: false
      }));
    }
  }, [chatSession]);
  return {
    miawHooksData: {
      miawChatStartData,
      miawChatRetrieveData,
      miawChatEndData,
      miawChatMessageData
    },
    miawHooksLoading: {
      miawChatStartLoading,
      miawChatRetrieveLoading,
      miawChatEndLoading,
      miawChatMessageLoading
    },
    miawHooksError: {
      miawChatStartError,
      miawChatRetrieveError,
      miawChatEndError,
      miawChatMessageError
    },
    miawHooksTriggers: {
      triggerMiawStartChat,
      miawChatRetrieveTrigger,
      triggerMIAWChatEnd,
      triggerMIAWChatMessage
    }
  };
};

export { useMIAWHooks };
//# sourceMappingURL=src-hooks-useMIAWHooks.js.map
